import React, { useMemo, useState } from "react";
import { api } from "./axiosClient";
import { toast } from "react-toastify";

export default function ExportEnquiryPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    product: "",
    quantity: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const productOptions = useMemo(
    () => [
      { value: "Raw Makhana", label: "Raw Makhana (Fox Nuts)" },
      { value: "Roasted Makhana", label: "Roasted Makhana" },
      { value: "Flavored Makhana", label: "Flavored Makhana" },
      { value: "Gift Packs", label: "Gift Packs" },
    ],
    []
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { name, email, phone, country, product, quantity } = form;

    if (!name || !email || !phone || !country || !product || !quantity) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        fullName: name,
        email,
        phone,
        country,
        product,
        quantity,
        message: form.message,
      };

      // ✅ same backend route
      await api.post("/api/enquiries", payload);

      setSubmitted(true);
      toast.success("Enquiry submitted successfully!");
      setForm({
        name: "",
        email: "",
        phone: "",
        country: "",
        product: "",
        quantity: "",
        message: "",
      });
    } catch (error) {
      console.error("Enquiry failed:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Upgraded thank-you screen (still simple, but premium)
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF5] px-4 py-12">
        <div className="mx-auto max-w-2xl rounded-2xl bg-white/80 p-7 text-center shadow-sm ring-1 ring-black/5 backdrop-blur">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#3E8E41]/15 text-[#1E5631] ring-1 ring-black/5">
            ✓
          </div>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-[#1E5631]">
            Thank you!
          </h1>
          <p className="mt-2 text-black/60">
            Your export enquiry has been received. We’ll get back to you within{" "}
            <span className="font-medium text-black/70">24–48 hours</span>.
          </p>

          <div className="mt-6 rounded-xl border border-black/5 bg-[#FAFAF5] p-4 text-left">
            <p className="text-sm font-semibold text-[#1E5631]">
              What happens next?
            </p>
            <ol className="mt-2 space-y-2 text-sm text-black/60">
              <li>1) We review your requirement (product, quantity, country).</li>
              <li>2) We share quotation + MOQ + packaging options.</li>
              <li>3) Sampling / QC and shipment timeline confirmation.</li>
            </ol>
          </div>

          <button
            onClick={() => setSubmitted(false)}
            className="mt-6 w-full rounded-xl bg-[#1E5631] py-3 font-semibold text-white shadow-sm transition hover:brightness-[1.05]"
          >
            Submit another enquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF5]">
      {/* Header band */}
      <div className="relative overflow-hidden border-b border-black/5">
        <div className="absolute inset-0 opacity-[0.08]">
          <div className="absolute -top-28 -left-24 h-80 w-80 rounded-full bg-[#3E8E41]" />
          <div className="absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-[#D4AF57]" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 py-10 sm:py-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-sm text-[#1E5631] shadow-sm ring-1 ring-black/5">
              <span className="h-2 w-2 rounded-full bg-[#3E8E41]" />
              Global Trade • Bulk Supply • Private Label
            </div>

            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-[#1E5631] sm:text-4xl">
              Send Export Enquiry
            </h1>
            <p className="mt-2 text-base leading-relaxed text-black/60">
              Share your requirement and destination. We’ll respond with pricing,
              MOQ, packaging options, and shipping timelines.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <InfoChip label="Quality-checked lots" />
              <InfoChip label="Export-ready packaging" />
              <InfoChip label="On-time dispatch" />
            </div>
          </div>
        </div>
      </div>

      {/* Main section */}
      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Form card */}
          <div className="rounded-2xl bg-white/80 p-5 shadow-sm ring-1 ring-black/5 backdrop-blur sm:p-7">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Full Name"
                  required
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your name"
                />

                <Field
                  label="Email"
                  required
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                />

                <Field
                  label="Phone"
                  required
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Country code + number"
                />

                <Field
                  label="Country"
                  required
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  placeholder="e.g., UAE, USA, UK"
                />

                <Select
                  label="Product"
                  required
                  name="product"
                  value={form.product}
                  onChange={handleChange}
                  options={productOptions}
                />

                <Field
                  label="Quantity (kg)"
                  required
                  name="quantity"
                  value={form.quantity}
                  onChange={handleChange}
                  placeholder="e.g., 500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-black/70">
                  Additional Message{" "}
                  <span className="text-black/40">(optional)</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="MOQ preference, packaging type, destination port/city, timeline, target price, etc."
                  className="w-full resize-none rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] text-black/80 shadow-sm outline-none transition focus:border-[#3E8E41] focus:ring-4 focus:ring-[#3E8E41]/15"
                />
                <p className="mt-2 text-xs text-black/45">
                  Tip: Mention destination city/port for faster quotation.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group relative w-full overflow-hidden rounded-xl bg-[#1E5631] px-5 py-3.5 text-base font-semibold text-white shadow-sm transition hover:brightness-[1.05] disabled:cursor-not-allowed disabled:opacity-70"
              >
                <span className="relative z-10">
                  {loading ? "Submitting..." : "Submit Enquiry"}
                </span>
                <span className="absolute inset-y-0 right-0 w-24 translate-x-12 bg-white/15 blur-xl transition group-hover:translate-x-0" />
              </button>
            </form>
          </div>

          {/* Side panel */}
          <div className="rounded-2xl bg-white/70 p-5 shadow-sm ring-1 ring-black/5 sm:p-7">
            <h3 className="text-lg font-semibold text-[#1E5631]">
              What happens next?
            </h3>

            <ol className="mt-4 space-y-3 text-sm text-black/65">
              <li className="flex gap-3">
                <StepDot n="1" />
                <div>We review your requirement and destination.</div>
              </li>
              <li className="flex gap-3">
                <StepDot n="2" />
                <div>We share MOQ, pricing, packaging, and lead time.</div>
              </li>
              <li className="flex gap-3">
                <StepDot n="3" />
                <div>Sampling/QC (if needed) and shipment planning.</div>
              </li>
            </ol>

            <div className="mt-6 rounded-xl border border-black/5 bg-[#FAFAF5] p-4">
              <p className="text-sm font-medium text-black/70">
                Want a faster response?
              </p>
              <p className="mt-1 text-sm text-black/55">
                Add your WhatsApp number + destination city/port in the message.
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Tag text="Bihar-sourced" />
              <Tag text="Bulk lots" />
              <Tag text="Private label" />
              <Tag text="Export-ready packaging" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- UI helpers ---------- */

function InfoChip({ label }) {
  return (
    <div
      className="flex items-center gap-2 rounded-xl
      border-2 border-[#1E5631]/20
      bg-white px-4 py-2.5
      text-sm font-semibold text-[#1E5631]
      shadow-sm transition
      hover:border-[#1E5631] hover:bg-[#1E5631]/5"
    >
      <span className="flex h-5 w-5 items-center justify-center rounded-full
        bg-[#1E5631] text-xs text-white">
        ✓
      </span>
      {label}
    </div>
  );
}


function StepDot({ n }) {
  return (
    <div className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#D4AF57]/20 text-xs font-semibold text-[#1E5631] ring-1 ring-black/5">
      {n}
    </div>
  );
}

function Tag({ text }) {
  return (
    <span
      className="rounded-full
      bg-[#FAFAF5]
      px-4 py-1.5
      text-xs font-semibold tracking-wide
      text-[#1E5631]
      border border-[#1E5631]/25
      uppercase"
    >
      {text}
    </span>
  );
}

function Field({
  label,
  required,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-black/70">
        {label} {required && <span className="text-[#3E8E41]">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] text-black/80 shadow-sm outline-none transition focus:border-[#3E8E41] focus:ring-4 focus:ring-[#3E8E41]/15"
      />
    </div>
  );
}

function Select({ label, required, name, value, onChange, options }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-black/70">
        {label} {required && <span className="text-[#3E8E41]">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] text-black/70 shadow-sm outline-none transition focus:border-[#3E8E41] focus:ring-4 focus:ring-[#3E8E41]/15"
      >
        <option value="">-- Select Product --</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
